<script setup lang="ts">
import {computed, ref} from "vue";
import {Plus, X} from "@lucide/vue";
import {VidalEntry} from "@/medication-checkup/interfaces/patient-profile";
import MedicationCheckupVidalSearch from "@/medication-checkup/components/MedicationCheckupVidalSearch.vue";

/**
 * The Vidal entries of one kind — allergies or pathologies — as chips, with
 * a dashed chip that opens a search in the Vidal referential.
 *
 * Picking a result adds its chip; the cross of a chip drops it. Frozen, the
 * chips read in a light tint and nothing opens.
 */

const props = defineProps<{
    kind: 'allergy' | 'pathology';
    entries: VidalEntry[];
    frozen?: boolean;
}>();

const emit = defineEmits<{
    'update:entries': [entries: VidalEntry[]];
}>();

/**
 * The invitation to add an entry of each kind.
 */
const ADD = {
    allergy: 'Ajouter une allergie',
    pathology: 'Ajouter une pathologie',
};

const searching = ref(false);

const picked = (entry: VidalEntry): boolean => {
    return props.entries.some(current => current.vidal_id === entry.vidal_id);
};

const pick = (entry: VidalEntry) => {
    if (!picked(entry)) {
        emit('update:entries', [...props.entries, entry]);
    }

    searching.value = false;
};

const drop = (entry: VidalEntry) => {
    emit('update:entries', props.entries.filter(current => current.vidal_id !== entry.vidal_id));
};

const chipClasses = computed(() => {
    return props.frozen
        ? 'border-gray-light bg-gray-light text-primary'
        : 'border-primary bg-primary text-white';
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center gap-1.5">
      <span
        v-for="entry in entries"
        :key="entry.vidal_id"
        class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-solid text-xs font-medium"
        :class="chipClasses"
      >
        {{ entry.title }}
        <button
          v-if="!frozen"
          class="p-0 rounded-full opacity-80 hover:opacity-100"
          :title="`Retirer ${entry.title}`"
          @click="drop(entry)"
        >
          <X class="w-3 h-3" />
        </button>
      </span>

      <span
        v-if="frozen && entries.length === 0"
        class="text-xs italic text-muted-foreground"
      >
        Aucune
      </span>

      <button
        v-if="!frozen && !searching"
        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border-[1.5px] border-dashed border-gray bg-white text-gray-dark text-xs font-medium hover:border-primary hover:text-primary"
        @click="searching = true"
      >
        <Plus class="w-3 h-3" />
        {{ ADD[kind] }}
      </button>
    </div>

    <MedicationCheckupVidalSearch
      v-if="!frozen && searching"
      :kind="kind"
      :picked="picked"
      @pick="pick"
      @dismiss="searching = false"
    />
  </div>
</template>
